import { AllocationOrigin, BulkMoveCapture, TimingOrigin } from './allocation-event-log.model';

export interface BaseSchedulerEvent {
    start_date: string;
    end_date: string;
    text: string;
    id: string;
}

export type MissingFromEcaseReason = 'movement_related' | 'care_plan_bug';

export interface MissingFromEcaseFlags {
    is_missing_from_ecase?: boolean;
    missing_from_ecase_reason?: MissingFromEcaseReason | string | null;
    missing_from_ecase_read_only?: boolean;
    missing_from_ecase_source?: string | null;
}

export interface TaskItem extends BaseSchedulerEvent {
    carer_string: string;
    carer_name: string;
    unallocated_key: number;
    CCQuoteDetailID: string;
    css_ecase: string;
    css_custom: string;
    residentID: string | null;
    client_name: string | null;
    client_gender: string | null;
    section_key: number;
    section_label: string;
    is_hpac: boolean;
    is_pm_service: boolean;
    is_staff_event: boolean;
    suggested_carer_string: string | null;
    suggested_carer_name: string | null;
    preferred_carers: string[];
    excluded_carers: string[];
    is_2pa: boolean;
    partially_allocated: boolean; // New field to track if a 2PA service has only one carer allocated
    // ML action-log context (optional: synthetic/legacy constructors may omit them)
    activity_id?: string | null;      // eCase activity (service type) id
    activity_name?: string | null;
    ad_hoc?: boolean | null;          // one-off service (inverse recurrence proxy)
    staff_min?: number | null;        // required staff count (2PA when max > 1)
    staff_max?: number | null;
    is_break?: boolean;               // staff-break event ("Staff - Break ...")
    // ML action-log capture v2 (all optional). Written by the load,
    // auto-allocate, drag, bulk-move and care-plan-dialog paths and read ONLY
    // by the action log (ChangeManagementService): nothing else may read or
    // branch on them. Rebuilt with the item on every day load.
    client_preferred_gender?: string | null;    // eCase gender preferences as loaded
    client_excluded_gender?: string | null;
    loaded_at?: string | null;                  // when the day load that built this item ran (ISO-8601 UTC)
    loaded_date?: string | null;                // the day that load asked for (yyyy-MM-dd)
    log_allocation_origin?: AllocationOrigin;   // what last set the carer now pending
    log_timing_origin?: TimingOrigin;           // what last changed the time
    log_change_seq?: number;                    // session counter at the last mutation
    log_changed_at?: string;                    // browser clock at that mutation (ISO-8601 UTC)
    log_bulk?: BulkMoveCapture;                 // the bulk gesture behind a 'bulk_row' origin
    // status fields
    pending_allocation_change: boolean;
    pending_timing_change: boolean;
    pending_change: boolean;
    pending_change_conflicts_with: string | null;
    // initial data for reverting
    original_start_date: string;
    original_end_date: string;
    original_carer_string: string;
    conflict_key: string;
    readonly?: boolean;
    is_missing_from_ecase?: boolean;
    missing_from_ecase_reason?: MissingFromEcaseReason | string | null;
    missing_from_ecase_read_only?: boolean;
    missing_from_ecase_source?: string | null;
}

export interface TaskMoveAllowedResult {
    disallowReason?: string;
    classString: string;
}

// Staff-break events are ordinary scheduler rows identified only by their text.
// Shared so the break validator and the ML action log agree on what a break is.
export const isBreakEventText = (text: string | null | undefined): boolean => {
    if (!text) {
        return false;
    }
    return text.includes('Staff - Break') &&
        (text.includes('10 min paid') || text.includes('30 min unpaid'));
};

export const isSyntheticMissingService = (item: MissingFromEcaseFlags | null | undefined): boolean => {
    if (!item) {
        return false;
    }
    return item.is_missing_from_ecase === true || item.missing_from_ecase_read_only === true;
};
